import type { Currency } from '@ps/web-core-enums';
import type { ClubRecentTournamentSummary } from '@ps/web-shared-api-types/v0/SwHomeGames';
import { Link, Translation } from '@ps/web-shared-components';
import { differenceInMinutes } from 'date-fns';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';

import Arrow from '@swf/core/assets/icons/arrow.svg';
import { FormattedCurrencyAmount, stripHTML, useHomeGamesWidget } from '@swf/poker';

import { ClubRoute } from '../../../../types/common';

import {
  TXTCLI_Duration,
  TXTCLI_Minutes,
  TXTCLI_Players,
  TXTCLI_Prize_Pool
} from './ResultsList.messages.json';
import styles from './ResultsList.scss';

const ResultsList: React.FC = () => {
  const {
    params: { clubId }
  } = useRouteMatch<{ clubId: string }>();
  const { formatDate } = useIntl();
  const { widgetSelect } = useHomeGamesWidget();
  const summaries = useSelector(widgetSelect.clubSummariesByDate);

  return (
    <>
      {Object.keys(summaries)
        .sort((a, b) => +new Date(b) - +new Date(a))
        .map((date) => {
          const group = summaries[date] as ClubRecentTournamentSummary[];

          return (
            <div className={styles.section} key={date}>
              <div className={styles.date}>
                {formatDate(date, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  weekday: 'long'
                })}
              </div>
              {group.map(
                ({
                  tournId,
                  tournName,
                  numPlayers,
                  prizePool,
                  currency,
                  started,
                  closed,
                  seasonId,
                  scalePM,
                  playMoney
                }) => (
                  <Link
                    alias="homeGamesClubs"
                    values={{
                      clubId,
                      section: ClubRoute.Results,
                      subsection: `${seasonId}-${tournId}`
                    }}
                    key={tournId}
                    className={styles.row}
                  >
                    <div className={styles.cell}>{stripHTML(tournName) || tournId}</div>
                    <div className={styles.cell}>
                      <span className={styles.label}>
                        <Translation {...TXTCLI_Players} />
                      </span>
                      {numPlayers}
                    </div>
                    <div className={styles.cell}>
                      <span className={styles.label}>
                        <Translation {...TXTCLI_Prize_Pool} />
                      </span>
                      <FormattedCurrencyAmount
                        amount={prizePool}
                        scalePM={scalePM}
                        isPlayMoney={playMoney}
                        currency={currency as Currency}
                      />
                    </div>
                    <div className={styles.cell}>
                      <span className={styles.label}>
                        <Translation {...TXTCLI_Duration} />
                      </span>
                      {differenceInMinutes(new Date(`${closed}`), new Date(`${started}`))}{' '}
                      <Translation {...TXTCLI_Minutes} />
                    </div>
                    <div className={styles.cell}>
                      <Arrow />
                    </div>
                  </Link>
                )
              )}
            </div>
          );
        })}
    </>
  );
};

export default ResultsList;
